import Header from "components/layout/header";
import moment from "moment";
import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  notificationGet,
  notificationRead,
  updateNotificationCount,
  updateIsReadAllNotification,
  useUserData,
} from "store/slices/authSlice";
import { downloadPdfFromApi, encode } from "utils/HelperFunctions";
import CustomPlansIcon from "icons/header/CustomPlansIcon";
import PaymentAgreementIcon from "icons/header/PaymentAgreementIcon";
import PaymentLinkIcon from "icons/header/PaymentLinkIcon";
import ProcessPaymentIcon from "icons/header/ProcessPaymentIcon";
import Skeleton from "react-loading-skeleton";
import { useParams } from "react-router-dom";
import Drawer from "components/ui-component/SideDrawer";
import PaymentPreview from "components/payments/PaymentPreview";
import SubscriptionsPreview from "components/subscriptions/SubscriptionsPreview";
import ActionPreview from "components/actionCenter/ActionPreview";
import { paymentReceiptDownloadPdf } from "store/slices/paymentsSlice";
import { useLeadData, usePortalVanityUrl } from "store/slices/dashboardSlice";
import Button from "components/form/Button";
import { FilterTypes } from "types/common";
import NotificationMessage, {
  NotificationPreviewType,
} from "components/notifications/NotificationMessage";
import { formatPaymentListDate, formatRelativeTime } from "utils/DateFormat";
import { captureException } from "utils/sentry";
import { decode } from "utils/HelperFunctions";
import QuotesPreview from "components/actionCenter/QuotesPreview";
import InvoicePreview from "components/actionCenter/InvoicePreview";
import ProfileIcon from "icons/header/ProfileIcon";
import InvoiceIcon from "icons/header/InvoiceIcon";
import QuoteIcon from "icons/header/QuoteIcon";
import { useMediaQuery } from "react-responsive";
import SubscriptionIcon from "icons/header/SubscriptionIcon";
import { NotificationIcon } from "icons/header/NotificationIcon";

import {
  NotificationRaw,
  NotificationListItem,
  NotificationGroups,
} from "types/notification";
import { RootState } from "store/store";
import { TestIdScope, useTestAttrs, useTid } from "utils/testid";
import { CoinIcon } from "icons/sidebar/CoinIcon";
import OffersAvailable from "icons/header/OffersAvailable";

const NotificationContent = () => {
  const tid = useTid();
  const vanityUrl = usePortalVanityUrl();
  const rootAttrs = useTestAttrs("page", "Notifications page");
  const titleAttrs = useTestAttrs("title", "Notifications");
  const listAttrs = useTestAttrs("list", "Notification list");
  const loadingAttrs = useTestAttrs("loading", "Loading notifications");
  const userData = useUserData();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  let accountData = userData?.data?.accountData;
  const [filter, setFilter] = useState<FilterTypes>({
    offset: 0,
    limit: 20,
  });
  const isLessThan590 = useMediaQuery({ query: "(max-width:590px)" });
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activityList, setActivityList] = useState<NotificationGroups>({});
  const [isPaymentPreview, setIsPaymentPreview] = useState<boolean>(false);
  const [isPaymentPreviewId, setIsPaymentPreviewId] = useState("");
  const [isSubscriptionPreview, setIsSubscriptionPreview] =
    useState<boolean>(false);
  const [isSubscriptionPreviewId, setIsSubscriptionPreviewId] = useState("");
  const [isActionPreview, setIsActionPreview] = useState<boolean>(false);
  const [isActionPreviewId, setIsActionPreviewId] = useState("");
  const [isQuotePreview, setIsQuotePreview] = useState<boolean>(false);
  const [isQuotePreviewId, setIsQuotePreviewId] = useState("");
  const [isInvoicePreview, setIsInvoicePreview] = useState<boolean>(false);
  const [isInvoicePreviewId, setIsInvoicePreviewId] = useState<string>("");
  const [view, setView] = useState<string>("");
  const [isDownloading, setIsDownloading] = useState<string | number>("");
  const accountLeadData = useLeadData();
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
  // Page number the list on screen actually belongs to. It only moves once new
  // rows have arrived, so the "Viewing 21-40" caption can never describe rows
  // the customer is not looking at.
  const [loadedPage, setLoadedPage] = useState<number>(1);
  // Set when the list could not be fetched, so the page can say so instead of
  // silently leaving the previous rows on screen.
  const [loadError, setLoadError] = useState<boolean>(false);
  // Re-render once a minute so "3 mins" / "2 hours" keep counting up instead of
  // freezing at whatever they read when the page was opened.
  const [, setNowTick] = useState<number>(0);
  useEffect(() => {
    const timer = setInterval(() => setNowTick((tick) => tick + 1), 60000);
    return () => clearInterval(timer);
  }, []);
  const { notificationCount, isReadAllNotification } = useAppSelector(
    (state: RootState) => state.auth
  );
  const dispatch = useAppDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const previousOffsetRef = useRef(filter.offset);

  const onPaymentDownload = async (id: string | number, receiptId?: any) => {
    setIsDownloading(id);
    let pid = encode(`${id}_${clusterId}_${new Date().getTime()}`, "pid_");
    try {
      let payload = {
        invoice_id: id,
        lead_id: accountLeadData?.lead_id,
        url: `${vanityUrl}view-payment/${pid}`,
        cluster_id: clusterId,
        isFromPortal: true,
      };

      await downloadPdfFromApi(
        () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
        `Receipt - ${receiptId || id}.pdf`,
        (loading) => setIsDownloading(loading ? id : "")
      );
    } catch (error: any) {
      console.log("payment download err: -->", error?.message);
    } finally {
      setIsDownloading("");
    }
  };

  const handleListFormat = async (data: NotificationRaw[]) => {
    let newList: NotificationGroups = {};
    if (data.length > 0) {
      setTotalCount(data[0]?.TotalCount || 0);
    }

    data.forEach((row: NotificationRaw) => {
      if (
        row?.event_type === "payment_agreement" ||
        row?.event_type === "payment_processed" ||
        row?.event_type === "payment_link" ||
        row?.event_type === "payment_scheduled" ||
        row?.event_type === "manual_payment" ||
        row?.event_type === "custom_plan" ||
        row?.event_type === "offer_available" ||
        row?.event_type === "quote" ||
        row?.event_type === "invoice" ||
        row?.event_type === "contact" ||
        row?.event_type === "payment_method" ||
        row?.event_type === "subscription"
      ) {
        const newDate = moment(row.event_date).format("MM/DD/YYYY");
        // The age is not stored here on purpose — it is worked out while
        // rendering so the "3 mins" style labels keep counting up on their own
        // instead of freezing at whatever they were when the page loaded.
        const item = {
          data: row,
          read: row.is_read,
          activity_id: row.id,
          type: row.event_type,
        } as NotificationListItem;

        if (newList[newDate]) {
          newList[newDate].push(item);
        } else {
          newList[newDate] = [item];
        }
      }
    });
    setActivityList(newList);
  };

  const markAllAsRead = async () => {
    let obj = { ...activityList };
    let updated = {} as NotificationGroups;
    Object.keys(obj).forEach((dateKey) => {
      const arr = obj[dateKey] || [];
      const updatedArr = arr.map((it) => ({
        ...it,
        read: 1,
        is_read: 1,
        data: { ...(it.data || {}), is_read: 1 },
      }));
      updated[dateKey] = updatedArr;
    });

    setActivityList(updated);
    await dispatch(updateIsReadAllNotification(false));
  };

  useEffect(() => {
    if (isReadAllNotification) {
      markAllAsRead();
    }
  }, [isReadAllNotification]);

  const readNotification = async (
    activiy_id: string | number = "",
    is_read = false,
    is_all_read = false
  ) => {
    try {
      let payload = {
        accounts_id: accountData.id,
        is_all_read: is_all_read,
        notificartion_read: true,
        is_read: is_read,
        activity_id: activiy_id,
      };

      let res = await dispatch(notificationRead(payload)).unwrap();
      if (res.status === 200 || res?.status === 201) {
        const obj = { ...activityList } as NotificationGroups;
        const updatedObj: NotificationGroups = {};
        if (is_all_read) {
          Object.keys(obj).forEach((dateKey) => {
            const arr = obj[dateKey] || [];
            const updtedArr = arr.map((it) => ({
              ...it,
              read: 1,
              is_read: 1,
              data: { ...(it.data || {}), is_read: 1 },
            }));
            updatedObj[dateKey] = updtedArr;
          });
        } else {
          Object.keys(obj).forEach((dateKey) => {
            const arr = obj[dateKey] || [];
            const updtedArr = arr.map((it) =>
              it.activity_id === activiy_id
                ? {
                    ...it,
                    read: 1,
                    is_read: 1,
                    data: { ...(it.data || {}), is_read: 1 },
                  }
                : { ...it }
            );
            updatedObj[dateKey] = updtedArr;
          });
        }

        setActivityList(updatedObj);
        if (notificationCount) {
          await dispatch(
            updateNotificationCount(is_all_read ? 0 : notificationCount - 1)
          );
        }
      }
    } catch (error) {
      console.log("readNotification error =====", error);
    }
  };

  const notificationItemKey = (item: NotificationListItem) =>
    String(
      item.activity_id ||
        item.id ||
        item.data?.id ||
        `${item.type || "notification"}-${item.data?.event_date || ""}-${
          item.data?.order_id ||
          item.data?.payment_link_id ||
          item.data?.invoice_id ||
          item.data?.quote_id ||
          item.data?.plan_id ||
          "item"
        }`
    );

  const createColumns = (data: NotificationListItem[]) => {
    return (
      <div className="w-full flex flex-col lg:gap-6 gap-4">
        {data.map((item: NotificationListItem) => {
          const itemKey = notificationItemKey(item);
          const isUnread = item?.data?.is_read === 0;
          return (
            <div
              className="text-txtPrimary w-full flex justify-between gap-[6px] lg:py-2 py-1 rounded-[7px] items-center hover:!bg-bgHover"
              key={itemKey}
              data-testid={tid(`item-${itemKey}`)}
              aria-label={`${isUnread ? "Unread" : "Read"} ${item.type || "notification"}`}
            >
              <div className="flex gap-3 items-center lg:w-fit max-w-[80%]">
                <div
                  className="w-10 h-10 bg-bgSecondary rounded-full p-[10px]"
                  aria-hidden="true"
                >
                  {item.type === "payment_agreement" ? (
                    <PaymentAgreementIcon />
                  ) : item.type === "payment_link" ? (
                    <PaymentLinkIcon />
                  ) : item.type === "payment_processed" ||
                    item.type === "manual_payment" ? (
                    <ProcessPaymentIcon />
                  ) : item.type === "payment_scheduled" ||
                    item.type === "custom_plan" ? (
                    <CustomPlansIcon />
                  ) : item.type === "quote" ? (
                    <QuoteIcon />
                  ) : item.type === "invoice" ? (
                    <InvoiceIcon />
                  ) : item.type === "contact" ? (
                    <ProfileIcon />
                  ) : item.type === "subscription" ? (
                    <SubscriptionIcon />
                  ) : item.type === "payment_method" ? (
                    <CoinIcon
                      width={20}
                      height={20}
                      strokeColor="var(--color-blue)"
                    />
                  ) : item?.type === "offer_available" ? (
                    <OffersAvailable />
                  ) : null}
                </div>
                <div className="flex flex-col gap-[5px]">
                  <div className="lg:text-sm text-xs text-txtSecondary ">
                    <NotificationMessage
                      data={item.data}
                      onOpenPreview={openPreview}
                    />
                  </div>
                </div>
              </div>
              <div className="text-txtSecondary text-xs flex gap-[6px] items-center justify-center whitespace-nowrap shrink-0">
                {formatRelativeTime(item?.data?.event_date)}
                {isUnread ? (
                  <div
                    className="w-[6px] h-[6px] bg-blue rounded-full cursor-pointer pr-[2px]"
                    onClick={() =>
                      readNotification(item?.activity_id || "", true)
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        readNotification(item?.activity_id || "", true);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    data-testid={tid(`item-${itemKey}-mark-read-button`)}
                    aria-label="Mark notification as read"
                  ></div>
                ) : (
                  <div
                    className="w-[6px] h-[6px]  rounded-full cursor-pointer pr-[2px]"
                    data-testid={tid(`item-${itemKey}-read-indicator`)}
                    aria-label="Notification read"
                  ></div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const fetchActivityList = async () => {
    try {
      let payload = {
        accounts_id: userData?.data?.accountData?.id,
        offset: filter.offset,
        limit: filter.limit,
      };
      let res: any = await dispatch(notificationGet(payload)).unwrap();
      const { data, status } = res.body;

      if (status) {
        await handleListFormat(data);
        setLoadedPage(filter.offset / filter.limit + 1);
        setLoadError(false);
      } else {
        setLoadError(true);
        reportLoadFailure(
          "Notifications request returned an unsuccessful status"
        );
      }
      setIsTableLoading(false);
      setIsLoading(false);
    } catch (error) {
      setIsTableLoading(false);
      setIsLoading(false);
      setLoadError(true);
      reportLoadFailure("Notifications request failed", error);
      console.log("onGetNotification error", error);
    }
  };

  // Send the failure to Sentry so a broken notifications list shows up on its
  // own instead of waiting for someone to report it. captureException drops
  // raw Axios errors on purpose, so the failure is raised as a plain Error and
  // the request details travel alongside it as context.
  const reportLoadFailure = (message: string, error?: unknown) => {
    const cause = error as
      | { message?: string; code?: string; response?: { status?: number } }
      | undefined;

    captureException(new Error(message), {
      tags: { area: "notifications", operation: "get-notifications" },
      extra: {
        offset: filter.offset,
        limit: filter.limit,
        accountsId: userData?.data?.accountData?.id,
        causeMessage: cause?.message,
        causeCode: cause?.code,
        responseStatus: cause?.response?.status,
      },
    });
  };

  // Re-request the page the customer is actually looking at.
  const retryLoad = () => {
    setFilter({ ...filter, offset: (loadedPage - 1) * filter.limit });
  };

  useEffect(() => {
    setIsLoading(true);
    fetchActivityList();
  }, [filter]);

  useLayoutEffect(() => {
    if (previousOffsetRef.current === filter.offset) return;

    previousOffsetRef.current = filter.offset;
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [filter.offset]);

  // The shared wording component tells us which preview to show; this page
  // drives its own drawers, so it maps that onto local state.
  const openPreview = (type: NotificationPreviewType, id: string | number) => {
    switch (type) {
      case "agreement":
      case "payment_link":
        setIsActionPreviewId(`${id}`);
        setView(type);
        setIsActionPreview(true);
        break;
      case "payment":
        setIsPaymentPreviewId(`${id}`);
        setIsPaymentPreview(true);
        break;
      case "invoice":
        setIsInvoicePreviewId(`${id}`);
        setIsInvoicePreview(true);
        break;
      case "quotes":
        setIsQuotePreviewId(`${id}`);
        setIsQuotePreview(true);
        break;
      case "subscription":
        setIsSubscriptionPreviewId(`${id}`);
        setIsSubscriptionPreview(true);
        break;
    }
  };


  return (
    <div className="w-full h-full flex flex-col" {...rootAttrs}>
      <Header
        headerContent={
          <div className="w-full flex items-center justify-between lg:p-0 pt-3 px-5">
            <div
              className="lg:text-[32px] sm:text-3xl text-2xl leading-[44px] text-txtPrimary font-medium -tracking-[0.72px]"
              {...titleAttrs}
            >
              Notifications
            </div>
            <div
              className="cursor-pointer text-blue hover:!text-btnPrimaryHover text-sm underline lg:hidden block"
              onClick={() => readNotification("", false, true)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  readNotification("", false, true);
                }
              }}
              role="button"
              tabIndex={0}
              data-testid={tid("mark-all-read-mobile-button")}
              aria-label="Mark all notifications as read"
            >
              Mark all as read
            </div>
          </div>
        }
      />
      {/* Fill whatever room the layout actually gives us instead of measuring
          against the whole window. The old fixed heights ignored the debt
          collector notice at the top, so whenever that notice was showing the
          page ran ~90px too long and pushed the paging row underneath the
          fixed footer bar. */}
      <div
        className={`lg:pt-[93px] flex-1 min-h-0 lg:px-10 px-5 flex flex-col lg:pb-2 pb-2 ${
          // On a phone the surrounding container reserves more room for the
          // footer bar than the bar needs, which left a wide empty band under
          // the paging row. Claim most of it back and keep a small gap.
          "-mb-[14px] lg:mb-0"
        }`}
      >
        <div
          ref={scrollContainerRef}
          className="overflow-y-scroll h-full"
          {...listAttrs}
        >
          {isLoading ? (
            <div {...loadingAttrs}>
              {[1, 2, 3, 4].map((ind) => (
                <div
                  className="lg:pt-10 pt-[30px]"
                  key={ind}
                  data-testid={tid(`loading-group-${ind}`)}
                >
                  <div className="text-2xl lg:pb-8 pb-4 font-medium text-txtPrimary">
                    <Skeleton width={140} height={34} />
                  </div>
                  {[1, 2, 3, 4].map((inde) => (
                    <div
                      className="w-full flex flex-col lg:gap-10 gap-5 lg:pb-10 pb-5"
                      key={inde}
                    >
                      <div className="flex items-center gap-3">
                        <div>
                          <Skeleton circle width={40} height={40} />
                        </div>
                        <div className="flex flex-col gap-[2px] flex-1 min-w-0">
                          <Skeleton
                            className="w-full max-w-[200px]"
                            height={25}
                          />
                          <Skeleton
                            className="w-full max-w-[500px]"
                            height={25}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : loadError ? (
            <LoadErrorState onRetry={retryLoad} />
          ) : !activityList || Object.keys(activityList).length === 0 ? (
            <EmptyState />
          ) : (
            <div className="pb-4">
              {activityList &&
                Object.keys(activityList).length > 0 &&
                Object.keys(activityList).map((item: any) => (
                  <div
                    className="lg:pt-10 pt-2"
                    key={item}
                    data-testid={tid(`group-${item}`)}
                  >
                    <div
                      className="lg:text-2xl text-xl lg:pb-10 pb-2 font-medium text-txtPrimary"
                      data-testid={tid(`group-${item}-title`)}
                    >
                      {moment(item).isSame(moment(), "day")
                        ? "Today"
                        : moment(item).isSame(
                              moment().subtract(1, "days"),
                              "day"
                            )
                          ? "Yesterday"
                          : formatPaymentListDate(
                              item,
                              userData?.data?.accountData?.timezone,
                              "long"
                            )}
                    </div>
                    {createColumns(activityList[item])}
                  </div>
                ))}
            </div>
          )}
        </div>
        {/* Nothing to page through on an empty list, and a failed load has no
            trustworthy count to show. */}
        {!loadError &&
          (isLoading || Object.keys(activityList || {}).length > 0) && (
            <PaginationComponent
              perPage={filter.limit}
              currentPage={loadedPage}
              totalCount={totalCount || 0}
              // Both steps are measured from the page that is actually on
              // screen, not from the last one requested, so a load that never
              // arrived cannot make the next click skip a page.
              onNextClick={(e) => {
                setFilter({
                  ...filter,
                  offset: loadedPage * filter.limit,
                });
              }}
              onPreviousClick={(e) => {
                setFilter({
                  ...filter,
                  offset: Math.max(0, (loadedPage - 2) * filter.limit),
                });
              }}
              isTableLoading={isTableLoading}
              isLoading={isLoading}
            />
          )}
      </div>
      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={isPaymentPreview}
        size={!isLessThan590 ? "590px" : `100vw`}
        onClose={() => {
          setIsPaymentPreview(false);
          setIsPaymentPreviewId("");
        }}
        overlayColor="#000"
      >
        <div
          className="contents"
          data-testid={tid("payment-preview-drawer")}
          aria-label="Payment preview drawer"
        >
          <PaymentPreview
            id={isPaymentPreviewId}
            setId={setIsPaymentPreviewId}
            show={isPaymentPreview}
            setShow={setIsPaymentPreview}
            isDownloading={isDownloading}
            onDownloadCLick={(id: any, receiptId: any) =>
              onPaymentDownload(id, receiptId)
            }
          />
        </div>
      </Drawer>
      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={isSubscriptionPreview}
        size={!isLessThan590 ? "590px" : `100vw`}
        onClose={() => {
          setIsSubscriptionPreview(false);
          setIsSubscriptionPreviewId("");
        }}
        overlayColor="#000"
      >
        <div
          className="contents"
          data-testid={tid("subscription-preview-drawer")}
          aria-label="Subscription preview drawer"
        >
          <SubscriptionsPreview
            id={Number(isSubscriptionPreviewId)}
            setId={setIsSubscriptionPreviewId}
            show={isSubscriptionPreview}
            setShow={setIsSubscriptionPreview}
            setHandleDrawerSize={() => {}}
            isScheduled={false}
          />
        </div>
      </Drawer>
      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={isActionPreview}
        size={!isLessThan590 ? "590px" : `100vw`}
        onClose={() => {
          setIsActionPreview(false);
          setIsActionPreviewId("");
        }}
        overlayColor="#000"
      >
        <div
          className="contents"
          data-testid={tid("action-preview-drawer")}
          aria-label="Action preview drawer"
        >
          <ActionPreview
            id={isActionPreviewId}
            setId={setIsActionPreviewId}
            show={isActionPreview}
            setShow={setIsActionPreview}
            view={view}
            setView={setView}
          />
        </div>
      </Drawer>

      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={isQuotePreview}
        size={!isLessThan590 ? "590px" : `100vw`}
        onClose={() => {
          setIsQuotePreview(false);
          setIsQuotePreviewId("");
        }}
        overlayColor="#000"
      >
        <div
          className="contents"
          data-testid={tid("quote-preview-drawer")}
          aria-label="Quote preview drawer"
        >
          <QuotesPreview
            id={isQuotePreviewId}
            slug={isQuotePreviewId}
            setId={setIsQuotePreviewId}
            show={isQuotePreview}
            setShow={setIsQuotePreview}
            isSlug={false}
          />
        </div>
      </Drawer>

      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={isInvoicePreview}
        size={!isLessThan590 ? "590px" : `100vw`}
        onClose={() => {
          setIsInvoicePreview(false);
          setIsInvoicePreviewId("");
        }}
        overlayColor="#000"
      >
        <div
          className="contents"
          data-testid={tid("invoice-preview-drawer")}
          aria-label="Invoice preview drawer"
        >
          <InvoicePreview
            id={isInvoicePreviewId}
            slug={isInvoicePreviewId}
            setId={setIsInvoicePreviewId}
            setIsPreview={setIsInvoicePreview}
            isPreview={isInvoicePreview}
            isSlug={false}
          />
        </div>
      </Drawer>
    </div>
  );
};

/* Shown when the customer genuinely has no notifications yet — usually a brand
   new account. Without this the page was simply blank, which reads as broken. */
const EmptyState = () => {
  const tid = useTid();
  const isLessThan590 = useMediaQuery({ query: "(max-width:590px)" });
  return (
    <div
      className="text-center h-full min-h-[320px] grid place-items-center lg:px-0 px-5"
      data-testid={tid("empty-state")}
    >
      <div className="h-fit w-fit flex items-center flex-col justify-center gap-10">
        <NotificationIcon
          width={isLessThan590 ? 80 : 100}
          height={isLessThan590 ? 80 : 100}
          strokeColor="var(--color-stroke-active)"
        />
        <div>
          <p className="lg:text-2xl text-xl text-txtPrimary font-semibold mb-4">
            No notifications yet
          </p>
          <p className="lg:text-lg text-base text-txtParagraph">
            When there is an update about your payments, plans or profile, it
            will appear here.
          </p>
        </div>
      </div>
    </div>
  );
};

/* Shown when the list could not be loaded. The previous behaviour was to leave
   the old rows on screen with no warning, so the customer had no way to tell
   that anything had gone wrong. */
const LoadErrorState = ({ onRetry }: { onRetry: () => void }) => {
  const tid = useTid();
  const isLessThan590 = useMediaQuery({ query: "(max-width:590px)" });
  return (
    <div
      className="text-center h-full min-h-[320px] grid place-items-center lg:px-0 px-5"
      data-testid={tid("error-state")}
    >
      <div className="h-fit w-fit flex items-center flex-col justify-center gap-10">
        <NotificationIcon
          width={isLessThan590 ? 80 : 100}
          height={isLessThan590 ? 80 : 100}
          strokeColor="var(--color-stroke-active)"
        />
        <div>
          <p className="lg:text-2xl text-xl text-txtPrimary font-semibold mb-4">
            We could not load your notifications
          </p>
          <p className="lg:text-lg text-base text-txtParagraph">
            Something went wrong on our side. Please check your connection and
            try again.
          </p>
        </div>
        <Button
          text="Try again"
          className="lg:!px-11 lg:!py-[10px] !px-4 !py-2"
          onClick={onRetry}
          data-testid={tid("error-retry-button")}
          aria-label="Try loading notifications again"
        />
      </div>
    </div>
  );
};

interface PaginationComponentProps {
  perPage: number;
  currentPage: number;
  totalCount: number;
  onPreviousClick?: (e?: any) => void;
  onNextClick?: (e?: any) => void;
  isTableLoading?: boolean;
  isLoading?: boolean;
}

const PaginationComponent = ({
  totalCount,
  perPage,
  currentPage,
  onPreviousClick = () => {},
  onNextClick = () => {},
  isTableLoading,
  isLoading,
}: PaginationComponentProps) => {
  const tid = useTid();
  const paginationAttrs = useTestAttrs("pagination", "Notification pagination");
  let count: number = totalCount && perPage ? totalCount / perPage : 0;
  let totalPages = Math.ceil(count) || 0;

  return (
    <div
      className="lg:py-5 py-3 flex justify-between items-center lg:border-y border-t border-strokePrimary"
      {...paginationAttrs}
    >
      <div
        className="text-sm font-normal leading-5 text-txtPrimary"
        data-testid={tid("pagination-summary")}
      >{`Viewing ${(currentPage - 1) * perPage + 1}-${Math.min(
        totalCount,
        currentPage * perPage
      )} of ${totalCount} results`}</div>
      {/* Both buttons always render. Greying one out keeps the controls in a
          fixed place, which is what the design shows; hiding them made the row
          jump around between the first, middle and last page. */}
      <div className="flex items-center gap-2">
        <Button
          isDisabled={currentPage <= 1 || isTableLoading}
          variant="stroke-outline"
          text="Previous"
          className={`!py-1 !px-2 !text-sm !leading-normal ${
            currentPage <= 1 || isTableLoading
              ? "!text-txtSecondaryInActive"
              : ""
          }`}
          onClick={onPreviousClick}
          data-testid={tid("pagination-previous-button")}
          aria-label="Previous notifications page"
        />
        <Button
          isDisabled={currentPage >= totalPages || isTableLoading}
          variant="stroke-outline"
          text="Next"
          className={`!py-1 !px-2 !text-sm !leading-normal ${
            currentPage >= totalPages || isTableLoading
              ? "!text-txtSecondaryInActive"
              : ""
          }`}
          onClick={onNextClick}
          data-testid={tid("pagination-next-button")}
          aria-label="Next notifications page"
        />
      </div>
    </div>
  );
};

const Notification = () => (
  <TestIdScope scope="notifications">
    <NotificationContent />
  </TestIdScope>
);

export default Notification;
